import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Download, Loader2, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

interface AiSupportLogEntry {
  id: string;
  created_at: string;
  user_name: string | null;
  question: string;
  answer: string;
}

export default function AdminSupportLogsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<AiSupportLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(
    async (retryCount = 0) => {
      const maxRetries = 3;
      const retryDelayMs = 2000;
      const isSchemaCacheError = (message: string) =>
        /schema cache|could not find the table|does not exist/i.test(message);

      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from("ai_support_logs")
          .select("id, created_at, user_name, question, answer")
          .order("created_at", { ascending: false });

        if (fetchError) throw fetchError;
        setEntries((data ?? []) as AiSupportLogEntry[]);
      } catch (err) {
        const message =
          (err as { message?: string })?.message ?? (err instanceof Error ? err.message : null) ?? String(err);
        if (isSchemaCacheError(message) && retryCount < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
          return fetchLogs(retryCount + 1);
        }
        setError(message);
        toast.error(t("admin.supportLogs.error"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const escapeCsvValue = (value: string) => `"${value.replace(/"/g, "\"\"")}"`;

  const exportToCSV = useCallback(() => {
    const header = [
      t("admin.supportLogs.date"),
      t("admin.supportLogs.name"),
      t("admin.supportLogs.question"),
      t("admin.supportLogs.answer"),
    ].map(escapeCsvValue).join(",");

    const rows = entries.map((entry) =>
      [
        formatDate(entry.created_at),
        entry.user_name?.trim() || t("admin.supportLogs.guest"),
        entry.question,
        entry.answer,
      ].map((value) => escapeCsvValue(value)).join(",")
    );

    const csvContent = `\uFEFF${[header, ...rows].join("\r\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "ai-support-logs.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [entries, t]);

  const backButtonClasses =
    "inline-flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-800/80 px-4 py-2 text-sm font-medium text-zinc-200 transition-colors hover:bg-zinc-700/80";

  const renderBackButton = () => (
    <button
      type="button"
      onClick={() => navigate("/")}
      className={backButtonClasses}
      aria-label={t("admin.feedback.backToHome")}
    >
      <ArrowLeft size={18} className="shrink-0" />
      {t("admin.feedback.backToHome")}
    </button>
  );

  return (
    <div className="mx-auto max-w-7xl text-zinc-100">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          {renderBackButton()}
          <button
            type="button"
            onClick={exportToCSV}
            disabled={entries.length === 0 || loading}
            className="inline-flex items-center gap-2 rounded-xl border border-purple-300/70 bg-purple-300 px-4 py-2 text-sm font-semibold text-zinc-950 transition-colors hover:bg-purple-200 hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t("admin.supportLogs.exportCsv")}
          >
            <Download size={18} className="shrink-0" />
            {t("admin.supportLogs.exportCsv")}
          </button>
        </div>
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-zinc-100">
            <Sparkles size={20} className="shrink-0 text-purple-400" />
            {t("admin.supportLogs.title")}
          </h1>
          <p className="text-sm text-zinc-400">
            {t("admin.supportLogs.subtitle")}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-zinc-700/60 bg-zinc-900/80 py-16">
          <Loader2 size={32} className="animate-spin text-purple-400" />
          <p className="text-sm text-zinc-400">
            {t("admin.supportLogs.loading")}
          </p>
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-8 text-center">
          <p className="text-red-400">{error}</p>
          <button
            type="button"
            onClick={() => fetchLogs()}
            className="mt-4 rounded-xl bg-red-500/20 px-4 py-2 text-sm font-medium text-red-400 hover:bg-red-500/30"
          >
            {t("buttons.retry")}
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-zinc-700/60 bg-zinc-900/80 py-16">
          <p className="font-medium text-zinc-200">
            {t("admin.supportLogs.empty")}
          </p>
          <p className="text-sm text-zinc-400">
            {t("admin.supportLogs.emptyHint")}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-zinc-700/60 bg-zinc-900/80">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] text-left text-sm">
              <thead>
                <tr className="border-b border-zinc-700/80 bg-zinc-800/60">
                  <th className="px-4 py-3 font-semibold text-zinc-200">
                    {t("admin.supportLogs.date")}
                  </th>
                  <th className="px-4 py-3 font-semibold text-zinc-200">
                    {t("admin.supportLogs.name")}
                  </th>
                  <th className="px-4 py-3 font-semibold text-zinc-200">
                    {t("admin.supportLogs.question")}
                  </th>
                  <th className="px-4 py-3 font-semibold text-zinc-200">
                    {t("admin.supportLogs.answer")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-zinc-800/80 align-top last:border-b-0"
                  >
                    <td className="px-4 py-4 text-zinc-300 whitespace-nowrap">
                      {formatDate(entry.created_at)}
                    </td>
                    <td className="px-4 py-4 font-medium text-zinc-100">
                      {entry.user_name?.trim() || t("admin.supportLogs.guest")}
                    </td>
                    <td className="px-4 py-4 text-zinc-300 whitespace-pre-wrap break-words">
                      {entry.question}
                    </td>
                    <td className="px-4 py-4 text-zinc-300 whitespace-pre-wrap break-words">
                      {entry.answer}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
